"use strict";

import _ from "lodash";
import { validateValue, isBasicObject } from "./systemValidation.js";

const REGEX_USER_SCHEMA_ID = /^([a-z_][a-z0-9_-]*:)(\$[a-z_][a-z0-9_-]*\.)*([a-z_][a-z0-9_-]*\.)+$/;

function getSubSchemas( entry )
{
	const res = [];
	if( entry.$$raw )
	{
		if( !entry.$$compiled )
			throw new Error( "Missing compiled version for schema:", entry.$$raw.$id );
		res.push( entry.$$compiled );
	}
	for( let e in entry )
		if( !e.startsWith( "$$" ) )
			res.push( ...getSubSchemas( entry[e] ) );
	return res;
}

function recompileSchemaSubtree( entry, compiledParent = null )
{
	if( !entry || typeof entry !== 'object' || !entry.$$raw )
		throw new Error( "Entry must be an object with '$$raw' property. Incorrect value given: ", entry );
	
	const compiled = compileSchema( entry.$$raw, compiledParent );
	entry.$$compiled = compiled;
	for( let e in entry )
		if( !e.startsWith( "$$" ) )
			this._recompileSubtree( entry[e], compiled );
}

function applyOverrideField( baseField, override )
{
	if( override.allownull === false )
		baseField.allownull = override.allownull;

	if( override.required === true )
		baseField.required = override.required;
	
	if( override.valuetype )
	{
		// Redundancy of valuetype definition allowed; just can't be different.
		if( baseField.valuetype && !override.valuetype.startsWith( baseField.valuetype ) )
			throw new Error( `Override field [${override.$id}] defines a valuetype [${override.valuetype}], which is not derived from the so far compiled base valuetype [${baseField.valuetype}] originating on schema [${baseField.owner}]` );
		baseField.valuetype = override.valuetype;
	}

	if( Array.isArray( override.validators ) )
	{
		if( !Array.isArray( baseField.validators ) )
			baseField.validators = [ ];
		Array.prototype.unshift.apply( baseField.validators, override.validators );	// Join arrays without duplication of first.
	}

	baseField.$mixins = _.merge( baseField.$mixins || {}, ( override || {} ).$mixins || { } );
}

function compileSchema( schema, compiledParentSchema )
{
	// validate params
	// overlay data to get compiled extension from parent-schema (adding new validators to parent list)
	// do NOT alter inputs - provide new object - all deep copies where required.
	// Any $mixins items on schema override the same key compiled schema's mixin, otherwise, pass thru from parent.

	if( !schema )
		throw new TypeError( "Missing [schema] paramater!" );

	const compiledSchema = _.cloneDeep( schema );
	compiledSchema.$mixins = _.defaultsDeep( compiledSchema.$mixins || {}, _.cloneDeep( ( compiledParentSchema || {} ).$mixins || { } ) );
	
	const rawFields = compiledSchema.fields;
	delete compiledSchema.fields;
	if( !Array.isArray( rawFields ) )
		throw new Error( `No fields found. Cannot compile schema "${schema.$id}"` );
	
	compiledSchema.compiledfields = [];
	compiledSchema.$schema = "$schema.$compiled."
	
	const baseFields = _.cloneDeep( ( compiledParentSchema || { } ).compiledfields || [] );
	const baseFieldsByName = { };
	const baseFieldsById = { };
	const baseFieldsOverridden = { };
	for( const bf of baseFields )
	{
		baseFieldsByName[bf.name] = bf;
		baseFieldsById[bf.$id] = bf;
	}
	
	for( let f of rawFields )
	{
		f = _.omit( f, [ 'owner', '$created', '$modified', '$version' ] );
		if( f.$schema.startsWith( '$field.$standard.' ) )
		{
			if( !f.hasOwnProperty( 'name' ) )
				throw new Error( `Invalid field object. A system standard field must have a 'name' property!` );

			if( baseFieldsByName[f.name] )
				throw new Error( `Conflict in the schema hierarchy. Field [${f.name}] is already defined in inheritance chain and then illegally defined again via non-overriding standard field [${f.$id}] in schema [${schema.$id}].` );
			
			compiledSchema.compiledfields.push( f );
		}
		else if( f.$schema.startsWith( '$field.$override.' ) )
		{
			if( !f.hasOwnProperty( 'overrides' ) )
				throw new Error( `Invalid field object. A system override field must have a 'overides' property!` );
			
			// You cannot have 2 override fields within the same schema target the same base field.
			// Note: Override fields cannot override other override fields. Their inheritance order is defined by their schema depth, not override chaining.
			if( baseFieldsOverridden[f.overrides] )
				throw new Error( `The field [${f.$id}] on schema [${f.owner}] attemts to override the same field [${f.overrides}] that is already being overridden by another field [${baseFieldsOverridden[f.overrides]}] on this same schema.` );

			const baseField = baseFieldsById[f.overrides];
			if( !baseField )
				throw new Error( `The field [${f.$id}] on schema [${f.owner}] attemts to override a non-existent base field [${f.overrides}].` );
			
			applyOverrideField( baseField, f )
			compiledSchema.compiledfields.push( baseField );

			baseFieldsOverridden[f.overrides] = f.$id;
		}
		else
			throw new Error( `Field schema [${f.$schema}] on field [${f.$id}] is not valid for any field definitions.` );
	}

	for( const f of baseFields )
	{
		if( !baseFieldsOverridden[f.$id] )
			compiledSchema.compiledfields.push( f );
	}

	return compiledSchema;
}

function getFieldDefinition( { definition = { }, currentPath = [], targetPath = [] } )
{
	// Create normalized valuetype and validators
	const resolvedValuetype = ( definition.itemvaluetype || definition.valuetype || [] );
	const thisValuetype = Array.isArray( resolvedValuetype ) ? resolvedValuetype : [ resolvedValuetype ] ;
	const resolvedValidators = ( definition.itemvalidators || definition.validators || [] );
	const thisValidators = Array.isArray( resolvedValidators ) ? resolvedValidators : [ resolvedValidators ] ;

	// Check if we reached the target.
	if( targetPath.length === 0 )	// Reached target: end of processing; return normalized definition.
		return { valuetype: thisValuetype, validators: thisValidators };
	
	// This is too much. We should NOT be doing this I think, unless we do a LOT more validator checking at this point..
	// if( thisValuetype && thisValuetype !== 'array' && thisValuetype !== 'object' )
	// 	throw { fieldPath : currentPath, message : `Ascendant field cannot be an array or object, so the field at the requested path cannot validly exist.` }

	const newCurrentPath = [ ...currentPath, targetPath[0] ];
	const newTargetPath = targetPath.slice( 1 );

	let valuetype = [], validators = [];

	for( let i=0; i < thisValidators.length; i++ )
	{
		const validator = thisValidators[i];
		const definition = validator.config || { };
		const res = getFieldDefinition( { definition, currentPath : newCurrentPath, targetPath : newTargetPath } );
		const commonValuetype = ( valuetype.length > 0 && res.valuetype.length > 0 ? _.intersection( valuetype, res.valuetype ) : undefined );
		if( Array.isArray( commonValuetype ) && commonValuetype.length === 0 )
			throw { fieldpath: newCurrentPath.join('/'), message: `Conflicting valuetypes found within this field's schema validation definition tree.` };
		if( res.valuetype.length > 0 )
			valuetype = commonValuetype || res.valuetype;
		validators.push( ...res.validators );
	}

	return { valuetype, validators };
}

class WNValidator
{
	/* // Structure for _schemas property
	this._schemas: {
		"item": {
			$$raw: { $schema : "$schema.$complete.", ... }
			$$compiled: { $schema : "$schema.$compiled.", ... }
			"product": {
				$$raw: { $schema : "$schema.$complete.", ... }
				$$compiled: { $schema : "$schema.$compiled.", ... }
			}
		}
	}
	*/

	/**
	 * Create new single-project validator
	 * @param {object} params
	 * @param {Object|Array} params.schemas Initial list (array) or lookup (object) of schemas. Order is irrelevant, but hierarchy must be complete.
	 * @throws {Error} Standard error, the hierarchy is incomplete, or schemas of multiple projects are given.
	 */
	constructor( { schemas } )
	{
		this._project = null;

		// Precompile all given schemas:
		this._schemas = { };
		if( isBasicObject( schemas ) )
			schemas = Object.values( schemas );
		
		if( !Array.isArray( schemas ) )
			throw new Error( "Must provide schemas as a lookup or array (empty is OK)" );
		
		if( schemas.length < 1 )
			return;	// No schemas is OK.
		
		this._project = ( ( ( schemas[0] || {} ).$id ) || "" ).split(":")[0];
		if( !this._project )
			throw new Error( "No schema project could be determined." );

		schemas.sort( ( a, b ) => ( a.$id < b.$id ? -1 : ( a.$id > b.$id ? 1 : 0 ) ) );

		schemas.forEach( s => this.addSchema( s ) );
	}

	/**
	 * Add Schema to This Validator
	 * @param {object} params
	 * @param {object} params.schema New schema to be added or replaced. Must have all hierarchy prerequisites already in place.
	 * @throws {Error} Standard error, if schema's parent is not already present, or project doesn't match, or invalid structure.
	 * @returns {Object} Newly added fully compiled schema.
	 */
	addSchema( schema )
	{
		if( !isBasicObject( schema ) || !schema.$id )
			throw new Error( "Invalid schema provided: "+ schema );
		if( !REGEX_USER_SCHEMA_ID.test( schema.$id ) )
			throw new Error( "Schema with invalid $id provided: "+ schema.$id );
		if( schema.$schema !== "$schema.$complete." || !Array.isArray( schema.fields ) )
			throw new Error( "Only COMPLETE schemas WITH a fields array are accepted." );
		
		const [ project, name ] = schema.$id.split( ':' );
		if( !this._project )
			this._project = project;
		else if( project !== this._project )
			throw new Error( "All schemas in a single validation instance must belong to the same project!" );

		const parentPath = name.replace( /\.$/, "" ).split( "." );
		const itemName = parentPath.pop( );
		
		const isRoot = parentPath.length === 0;
		const parentEntry = isRoot ? this._schemas : _.get( this._schemas, parentPath );

		if( !isRoot && ( !parentEntry || !parentEntry.$$raw ) )
			throw new Error( `Found schema without a valid parent: "${schema.$id}"` );
		
		if( !parentEntry[itemName] );
			parentEntry[itemName] = { };
		
		const myEntry = parentEntry[itemName];
		
		myEntry.$$raw = _.omit( schema, [ '$version', '$modified', '$created' ] );
		recompileSchemaSubtree( myEntry, parentEntry.$$compiled );

		return this.getCompiledSchema( name );
	}

	/**
	 * Get SINGLE Compiled Schema
	 * @param {object} params
	 * @param {string} params.name Name of schema to retrieve. If you include the project prefix (not required), it will be checked to match this validator.
	 * @throws {Error} Standard error, if retrieval or project match fails (if schema is not found, null is returned, without error)
	 * @returns {Object|null} Found compiled schema, or null if not found.
	 */
	getCompiledSchema( name )
	{
		const parts = name.split(":");
		if( parts.length > 1 && this._project && parts[0] !== this._project )
			throw new TypeError( `Cannot request schema for project "${parts[0]}". This validator is only for `+
				`project "${this._project}". NOTE: you don't have to include the "<project>:" portion at all!` );
		name = parts.pop( );
		const path = name.replace( /\.$/, "" ).split( "." );
		return _.get( this._schemas, path, { } ).$$compiled || null;
	}

	/**
	 * Get List or Lookup of Compiled Schemas (optionally just a sub-tree)
	 * @param {object} params
	 * @param {string} params.rootName Optional schema name to act as root of sub-tree to return. Otherwise, all are returned.
	 * @param {string} params.lookup Return the schemas as a lookup keyed on their schema names. Otherwise, array is returned.
	 * @throws {Error} Standard error, if retrieval fails (if schema is not found, empty list/lookup is returned without error)
	 * @returns {Object|Array} List (array) or Lookup (object) of requested schemas, or empty list/lookup, if not found.
	 */
	getCompiledSchemas( { rootName = "", lookup = false } )
	{
		const parts = rootName.split(":");
		if( parts.length > 1 && this._project && parts[0] !== this._project )
			throw new TypeError( `Cannot request schema for project "${parts[0]}". This validator is only for `+
				`project "${this._project}". NOTE: you don't have to include the "<project>:" portion at all!` );
		rootName = parts.pop( );

		const path = rootName.replace( /\.$/, "" ).split( "." ).filter( s => s.length > 0 );
		const entry = path.length === 0 ? this._schemas : _.get( this._schemas, path, { } );

		const resList = getSubSchemas( entry, lookup );

		if( lookup )
		{
			const res = { };
			for( const s of resList )
				res[ s.$id.split( ":" ).pop( ) ] = s;
			return res;
		}
		return resList;
	}

	/**
	 * Validate single field (great for form-field validation)
	 * NOTE-1: This method assumes that you have validated parent structures. It does not validate things along the path for you.
	 * NOTE-2: Always provide the fieldPath from the object that defines given schemaId (via $schema). If a nested object has its own $schema, start the path from there to validate a field within.
	 * @param {object} params
	 * @param {*} params.value The field VALUE (AT the given fieldPath) to be validated.
	 * @param {string|Array} params.fieldPath Path of the field relative to the object with the given schema.
	 * @param {string} params.schemaId Optional schema ID of the parent object (to validate this field against).
	 * @throws {object} Basic object error details: { fieldpath: "path/or/failure", schema: "failing.schema.name.", message: "details" }
	 * @returns {object} Resulting validated (unaltered) value
	 */
	validateField( { value, fieldPath, schemaId } )
	{
		const pathArray = Array.isArray( fieldPath ) ? fieldPath : ( typeof fieldPath === 'string' ? fieldPath.split( '/' ) : [] );
		const pathString = pathArray.join('/');	// Just to keep original path, for convenience.
		if( pathArray.length === 0 && schemaId )
			throw new TypeError( `You cannot ask to validate according to schema [${schemaId}] without providing a field path/name. When [schemaId] is provided, [fieldPath] is mandatory.` );
		
		let validators = [], valuetype = [];
		if( pathArray.length > 0 )
		{
			if( schemaId )
			{
				const searchFieldPath = pathString+'/';	// Add explicit '/' boundary for match.
				const schema = this.getCompiledSchema( schemaId );
				if( !schema )
					throw new TypeError( `Schema [${schemaId}] could not be found!` );
				
				// Get all the compiled schema fields which are within the given path.
				const fields = ( schema.compiledfields || [] ).filter( f => searchFieldPath.startsWith( f.name+'/' ) );
				const exactField = fields.find( f => f.name === pathString );
				
				if( exactField )
				{
					if( value === null && !exactField.allownull )
						throw { fieldpath: pathString, schema: schemaId, message: `Null value is not allowed for this field.` };
					
					if( typeof value === 'undefined' && exactField.required )
						throw { fieldpath: pathString, schema: schemaId, message: `This field is required, yet no value was given.` };
				}

				for( const def of fields )
				{
					const definition = _.pick( def, [ 'valuetype', 'validators' ] );
					const currentPath = def.name.split( '/' );
					const targetPath = pathString.substring( def.name.length + 1 ).split( '/' ).filter( item => item );
					let res = { };
					try {
						res = getFieldDefinition( { definition, currentPath, targetPath } );
					} catch( err ) {
						if( !( err instanceof Error ) )
							err.schema = schemaId;
							err.fieldpath = err.fieldpath || pathString;
						throw err;
					}
					const commonValuetype = ( valuetype.length > 0 && res.valuetype.length > 0 ? _.intersection( valuetype, res.valuetype ) : undefined );
					if( Array.isArray( commonValuetype ) && commonValuetype.length === 0 )
						throw { fieldpath: pathString, schema: schemaId, message: `Conflicting valuetypes found within this field's schema validation definitions.` };
					if( res.valuetype.length > 0 )
						valuetype = commonValuetype || res.valuetype;
					validators.push( ...res.validators );
				}
			}
		}

		return this._processValue( { value, validators, valuetype, fieldPath, schemaId } );
	}

	/**
	 * Validate an entire object (great for pre-submit form validation)
	 * @param {object} params
	 * @param {object} params.object The object to be validated
	 * @param {object} params.options Processing options (see specifics)
	 * @param {boolean} params.options.partialschemas Allow unrecognized fields in object, as long as their values are valid.
	 * @param {boolean} params.options.incomplete Allow incomplete object; ignore missing required fields.
	 * @param {boolean} params.options.allowabstractschemas Bypass [abstract] schema setting; allow object with abstract schema.
	 * @param {string} params.fieldPath Optional outer field-path of the object being validated for standardized failure result.
	 * @throws {object} Basic object error details: { fieldpath: "path/or/failure", schema: "failing.schema.name.", message: "details" }
	 * @returns {object} Resulting validated (unaltered) object
	 */
	validateObject( { object, options = { }, fieldPath = "" } )
	{
		const processingObject = _.clone( object );
		const resultObject = { };

		const schemaId = processingObject.$schema;
		if( schemaId )
		{
			const schema = this.getCompiledSchema( schemaId );
			if( !schema )
				throw { fieldpath: fieldPath, schema: schemaId, message: `Could not find schema [${schemaId}], required to validate this object.` };
			
			if( schema.abstract && !options.allowabstractschemas )
				throw { fieldpath: fieldPath, schema: schemaId, message: `Object${ fieldPath ? ` at [${fieldPath}]` : '' } has a schema [${schemaId}] which is marked as [abstract], this is not allowed, unless you set the [allowabstractschemas] option.` };
			
			// TODO:
			// const compiledFieldsLookup = schema.compiledfields.reduce( function( lookup, field ) {
			// 	lookup[field.name] = field;
			// 	return lookup;
			// }, { } );
			// TODO:
			//	We have to pre-add to the compiled schema:
			// 	All filled-in path fields (recursively) that have _subfields which are either:
			//		a) additions to the field definition
			// 		OR:
			// 	b) otherwise empty (new) "virtual" definitions that have ONLY the _subfields definition.
			// THEN, we remove the deep fields and process the root fields only, with additional
			// 	processing mechanisms to handle these new "_subfields".
			// 		E.g. if a virtual root field has fields outside of its _subfields definitions,
			// 			then it fails with unrecognized fields error.
			// 		E.g. if we are in parital processing, missing sub-fields are OK.
			
			for( const field of schema.compiledfields )
			{
				// 1. Get current value
				const deepFieldPathArray = field.name.split( '/' );
				const value = processingObject[field.name];
				// const value = utils.getDeepField( processingObject, deepFieldPathArray.slice( ) );


				delete processingObject[field.name];
				const currentFieldPath = ( fieldPath ? fieldPath +'/'+ field.name : field.name );
				
				// 2. Check against 'required' and 'allownull' definitions
				if( typeof value === 'undefined' )
				{
					if( field.required && !options.incomplete )
						throw { fieldpath: fieldPath +"/"+ field.name, schema: schemaId, message: `Object${ fieldPath ? ` at [${fieldPath}]` : '' } is missing required field [${currentFieldPath}], according to schema [${schema.$id}].`}
					continue;	// No value and nothing to process, but since it's optional, we're good to continue.
				}
				
				if( value === null )
				{
					if( !field.allownull )
						throw { fieldpath: fieldPath +"/"+ field.name, schema: schemaId, message: `Object${ fieldPath ? ` at [${fieldPath}]` : '' } has a null value at field [${currentFieldPath}], but this is not allowed according to schema [${schema.$id}].` };
					// We will let the null value be processed like all others. This allows for a more general solution with unstructured objects.
				}
				
				const valuetype = field.valuetype;
				const validators = field.validators;
				
				resultObject[field.name] = this._processValue( { value, valuetype, validators, fieldPath : currentFieldPath, schemaId } );
			}
		}

		// Process all remaining (unstructured or system) fields.
		for( let f in processingObject )
		{
			const value = processingObject[f];
			const currentFieldPath = ( fieldPath ? fieldPath +'/'+ f : f );

			// PROBLEM: We're now blindly trusting system fields, BUT when we implement mixins ($mixins : { "<id>" : <mixin> }) then we won't be validating those.
			if( f.startsWith( '$' ) )
			{
				if( f === '$mixins' )
				{
					if( !isBasicObject( value ) )
						throw { fieldpath: fieldPath +"/"+ f, schema: schemaId, message: "The $mixins system field may only have an object (lookup) value." };
					for( let mf in value )
						if( !isBasicObject( value[mf] ) )
							throw { fieldpath: fieldPath +"/"+ f, schema: schemaId, message: "Values within the $mix lookup may only be objects." };
					//throw utils.augmentError( new Error( `Mixins are not yet supported within object processing! Field at [${currentFieldPath}] cannot be processed.` ), null, 501 );	// 501: "Not Implemented"
					resultObject[f] = this._processValue( { value, fieldPath : currentFieldPath, schemaId } );
				}
				else
					resultObject[f] = _.clone( value );
			}
			else if( !schemaId || options.partialschemas )
				resultObject[f] = this._processValue( { value, fieldPath : currentFieldPath, schemaId } );
			else
				throw { fieldpath: fieldPath +"/"+ f, schema: schemaId, message: `Unrecognized field [${f}] found in structured object${ fieldPath ? ` at [${fieldPath}]` : '' }; this is only allowed in partial schema processing.` };
		}

		return resultObject;
	}
	
	/**
	 * INTERNAL: process a value.
	 * @param {*} params
	 * @returns 
	 */
	_processValue( { value, valuetype, validators = [], fieldPath = "", options = { }, schemaId } )
	{
		if( typeof value === 'undefined' )
			return;
		
		if( value === null )
			return null;
		
		let validationResult = validateValue( { value, valuetype, validators } );
		// console.log( "VALIDATION RESULT", JSON.stringify( validationResult, null, 2 ) );
		if( validationResult.result !== true )
		{
			fieldPath = fieldPath + validationResult.path
			throw { fieldpath: fieldPath, schema: schemaId, message: `Validation failed for field${ fieldPath ? ` at [${fieldPath}]` : '' }. ${validationResult.message}` };
		}
		
		if( Array.isArray( value ) )
		{
			const result = [];
			for( let i=0; i < value.length; i++ )
				result.push( this._processValue( { value : value[i], fieldPath : ( fieldPath +'/'+ i ), options, schemaId } ) );
			
			return result;
		}
		else if( value && typeof value === 'object' )
		{
			return this.validateObject( { object : value, options, fieldPath, schemaId } );
		}
		else
			return value;
	}
}

export default WNValidator;


// WEBPACK FOOTER //
// ./src/temp-validation/WNValidator.js